import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../lib/use-api';
import { api } from '../lib/api';
import { Card, ErrorNotice, Loading } from '../components/ui';

type Tab = 'control' | 'monetisation' | 'shaping';

interface Tier {
  name: string;
  downlinkKbps: number;
  uplinkKbps: number;
  durationMinutes: number;
  price: number;
}

interface ConfigPayload {
  controlMode: 'inline' | 'router_api';
  routerCredentials?: { vendor: string; host: string; username: string; password: string };
  wifiControl: { sessionTimeoutMinutes: number; maxDevicesPerGuest?: number; ssid?: string };
  monetisation: { enabled: boolean; currency: string; taxRatePct: number; tiers: Tier[]; revenueSharePct?: number };
  trafficShaping: {
    defaultGroup: string;
    groups: Array<{ name: string; downlinkKbps: number; uplinkKbps: number }>;
  };
}

/** Wireframe B6 — tabbed configuration with the control-mode selector on top. */
export function DeviceConfig() {
  const { deviceId = '' } = useParams();
  const loaded = useApi<{ config: ConfigPayload; baseVersion: number | null }>(`/devices/${deviceId}/config`);
  const [config, setConfig] = useState<ConfigPayload | null>(null);
  const [tab, setTab] = useState<Tab>('control');
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loaded.data) setConfig(loaded.data.config);
  }, [loaded.data]);

  if (loaded.loading || !config) return <Loading />;

  const update = (patch: Partial<ConfigPayload>) => setConfig({ ...config, ...patch });

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const result = await api.post<{ version: number; message: string }>(`/devices/${deviceId}/config`, {
        config,
        baseVersion: loaded.data?.baseVersion ?? undefined,
      });
      setSaved(result.message);
      loaded.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Configuration</h1>
        <p>
          <Link to={`/devices/${deviceId}`}>{deviceId}</Link> · changes reach the device on its next sync
        </p>
      </div>

      <ErrorNotice error={error} />
      {saved ? <div className="notice ok">{saved}</div> : null}

      <Card title="WiFi control mode">
        <div className="card-body">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button
              className={config.controlMode === 'inline' ? 'active' : ''}
              onClick={() => update({ controlMode: 'inline' })}
            >
              Inline gateway
            </button>
            <button
              className={config.controlMode === 'router_api' ? 'active' : ''}
              onClick={() => update({ controlMode: 'router_api' })}
            >
              Router API
            </button>
          </div>
          {config.controlMode === 'router_api' ? (
            <div style={{ marginTop: 14 }}>
              <div className="row">
                <div className="field">
                  <label>Router vendor</label>
                  <input
                    value={config.routerCredentials?.vendor ?? 'mikrotik'}
                    onChange={(e) =>
                      update({ routerCredentials: { ...defaultRouter(config), vendor: e.target.value } })
                    }
                  />
                </div>
                <div className="field">
                  <label>Router address</label>
                  <input
                    value={config.routerCredentials?.host ?? ''}
                    onChange={(e) => update({ routerCredentials: { ...defaultRouter(config), host: e.target.value } })}
                  />
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <label>Username</label>
                  <input
                    value={config.routerCredentials?.username ?? ''}
                    onChange={(e) =>
                      update({ routerCredentials: { ...defaultRouter(config), username: e.target.value } })
                    }
                  />
                </div>
                <div className="field">
                  <label>Password</label>
                  <input
                    type="password"
                    value={config.routerCredentials?.password ?? ''}
                    onChange={(e) =>
                      update({ routerCredentials: { ...defaultRouter(config), password: e.target.value } })
                    }
                  />
                  <div className="hint">Stored encrypted and sent only to your appliance.</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <div className="tabs">
        <button className={tab === 'control' ? 'active' : ''} onClick={() => setTab('control')}>
          WiFi control
        </button>
        <button className={tab === 'monetisation' ? 'active' : ''} onClick={() => setTab('monetisation')}>
          Monetisation
        </button>
        <button className={tab === 'shaping' ? 'active' : ''} onClick={() => setTab('shaping')}>
          Traffic shaping
        </button>
      </div>

      <Card>
        <div className="card-body">
          {tab === 'control' ? (
            <div className="row">
              <div className="field">
                <label>Session duration limit (minutes)</label>
                <input
                  type="number"
                  min={5}
                  value={config.wifiControl.sessionTimeoutMinutes}
                  onChange={(e) =>
                    update({
                      wifiControl: { ...config.wifiControl, sessionTimeoutMinutes: Number(e.target.value) },
                    })
                  }
                />
              </div>
              <div className="field">
                <label>Devices per guest</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={config.wifiControl.maxDevicesPerGuest ?? 2}
                  onChange={(e) =>
                    update({ wifiControl: { ...config.wifiControl, maxDevicesPerGuest: Number(e.target.value) } })
                  }
                />
              </div>
            </div>
          ) : null}

          {tab === 'monetisation' ? (
            <>
              <div className="field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto', minHeight: 'auto' }}
                    checked={config.monetisation.enabled}
                    onChange={(e) => update({ monetisation: { ...config.monetisation, enabled: e.target.checked } })}
                  />
                  Charge guests for higher-bandwidth access
                </label>
              </div>
              <div className="row">
                <div className="field">
                  <label>Currency</label>
                  <input
                    maxLength={3}
                    value={config.monetisation.currency}
                    onChange={(e) =>
                      update({ monetisation: { ...config.monetisation, currency: e.target.value.toUpperCase() } })
                    }
                  />
                </div>
                <div className="field">
                  <label>Tax rate (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={config.monetisation.taxRatePct}
                    onChange={(e) =>
                      update({ monetisation: { ...config.monetisation, taxRatePct: Number(e.target.value) } })
                    }
                  />
                </div>
              </div>

              <label>Pricing tiers</label>
              {config.monetisation.tiers.map((tier, index) => (
                <div className="row" key={index} style={{ marginBottom: 8 }}>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <input
                      value={tier.name}
                      placeholder="Tier name"
                      onChange={(e) => updateTier(index, { name: e.target.value })}
                    />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <input
                      type="number"
                      value={tier.price}
                      placeholder="Price"
                      onChange={(e) => updateTier(index, { price: Number(e.target.value) })}
                    />
                  </div>
                </div>
              ))}
              <button
                className="secondary small"
                style={{ marginTop: 8 }}
                onClick={() =>
                  update({
                    monetisation: {
                      ...config.monetisation,
                      tiers: [
                        ...config.monetisation.tiers,
                        { name: 'New tier', downlinkKbps: 10000, uplinkKbps: 2000, durationMinutes: 1440, price: 20 },
                      ],
                    },
                  })
                }
              >
                + Add tier
              </button>
              {config.monetisation.enabled && config.monetisation.tiers.length === 0 ? (
                <div className="notice info" style={{ marginTop: 12 }}>
                  Add at least one pricing tier before enabling paid access — otherwise guests reach a payment screen
                  with nothing to buy.
                </div>
              ) : null}
            </>
          ) : null}

          {tab === 'shaping' ? (
            <>
              <div className="field">
                <label>Default group for unpaid guests</label>
                <select
                  value={config.trafficShaping.defaultGroup}
                  onChange={(e) =>
                    update({ trafficShaping: { ...config.trafficShaping, defaultGroup: e.target.value } })
                  }
                >
                  {config.trafficShaping.groups.map((group) => (
                    <option key={group.name} value={group.name}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Group</th>
                    <th className="num">Download (kbps)</th>
                    <th className="num">Upload (kbps)</th>
                  </tr>
                </thead>
                <tbody>
                  {config.trafficShaping.groups.map((group, index) => (
                    <tr key={index}>
                      <td>{group.name}</td>
                      <td className="num">{group.downlinkKbps.toLocaleString()}</td>
                      <td className="num">{group.uplinkKbps.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </div>
      </Card>

      <button onClick={save} disabled={busy}>
        {busy ? 'Saving…' : 'Save changes'}
      </button>
    </>
  );

  function updateTier(index: number, patch: Partial<Tier>) {
    const tiers = config!.monetisation.tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier));
    setConfig({ ...config!, monetisation: { ...config!.monetisation, tiers } });
  }
}

function defaultRouter(config: ConfigPayload) {
  return config.routerCredentials ?? { vendor: 'mikrotik', host: '', username: '', password: '' };
}
